import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, MessageCircle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Admin tab: read chatbot conversations grouped by session_id. */
const AdminChatbot = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/chatbot/logs");
      setSessions(data.sessions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load conversations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalMessages = sessions.reduce((s, x) => s + (x.messages?.length || 0), 0);

  return (
    <div data-testid="admin-chatbot-panel" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="editorial-kicker">Andreas conversations</p>
          <h2 className="editorial-h2 text-2xl mt-2">
            {sessions.length} conversations · {totalMessages} messages
          </h2>
        </div>
        <Button
          type="button"
          onClick={load}
          variant="outline"
          disabled={loading}
          data-testid="admin-chatbot-refresh"
          className="h-10 rounded-lg"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          <span className="ml-2 text-[11px] tracking-[0.22em] uppercase">Refresh</span>
        </Button>
      </div>

      {loading && sessions.length === 0 ? (
        <div className="py-16 text-center text-foreground/50">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="py-16 text-center text-foreground/50 border border-dashed border-border rounded-xl">
          <MessageCircle className="w-8 h-8 mx-auto text-foreground/30" />
          <p className="mt-4 font-serif-display italic text-lg">No conversations yet.</p>
          <p className="mt-1 text-xs">Guest questions to Andreas will appear here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sessions.map((s) => (
            <div key={s.session_id} className="surface-card p-5" data-testid={`admin-chatbot-session-${s.session_id}`}>
              <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
                <p className="editorial-kicker text-foreground/55">
                  {s.language.toUpperCase()} · {new Date(s.last_at).toLocaleString()}
                </p>
                <p className="text-[11px] text-foreground/40 font-mono">{s.session_id.slice(0, 8)}…</p>
              </div>
              <div className="space-y-2">
                {s.messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div
                      className={
                        "max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed " +
                        (m.role === "user"
                          ? "bg-foreground text-background rounded-br-sm"
                          : "bg-background text-foreground rounded-bl-sm border border-border")
                      }
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminChatbot;
